import { useEffect, useRef, useState } from 'react'

import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { LinkProps } from '~/components/links'
import { resolveLink } from '~/components/serializers'
import { toURL } from '~/utils/urlResolver'

import AnimateIntoView, {
  AnimationType,
} from '../AnimateIntoView/AnimateIntoView'
import Button, { ButtonVariants } from '../Button/Button'
import type { ImageProps } from '../Image/Image'
import Image from '../Image/Image'
import Link from '../Link/Link'

import styles from './MagazineImage.module.css'

export interface MagazineImageProps {
  image: ImageProps & {
    defaultCaption?: {
      artistName: string
      artworkTitle: string
      artworkDate: number
    }
  }
  isLeadImage?: boolean
  testID?: string
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  priority?: boolean
  link?: LinkProps
  artwork?: any
}

export default function MagazineImage({
  image,
  isLeadImage,
  testID,
  verticalPadding,
  noPaddingBottom,
  priority,
  link,
  artwork,
}: MagazineImageProps) {
  const t = useTranslations('magazineImage')
  const orientation = useRef<string>('portrait')
  const [orientationsClass, setOrientationClass] = useState(orientation.current)
  const handleOrientation = (w: number, h: number) => {
    orientation.current = w / 0.9 > h ? 'landscape' : 'portrait'
  }

  useEffect(() => {
    setOrientationClass(orientation.current)
  }, [orientation])

  const defaultCaption = `${image.defaultCaption?.artworkTitle} © ${image.defaultCaption?.artistName} ${image.defaultCaption?.artworkDate}`
  const imageCaption =
    defaultCaption && !defaultCaption.includes('undefined')
      ? defaultCaption
      : image.credit?.text

  // When linked to an artwork, resolve the sell-page href from the artwork's
  // url object. Falls back to the incoming `link` if it's already resolved
  // (e.g. the ArtworkImage wrapper passes a pre-resolved string).
  const artworkHref =
    artwork && artwork.url ? toURL(artwork.url) : (link as any)

  const renderImage = () => {
    return (
      <>
        <div className={styles.imageWrap}>
          <Image
            asset={image.asset}
            alt={image.text}
            className={styles.image}
            getorientation={(w, h) => handleOrientation(w, h)}
            sizes={`(max-width: 560px) 100vw, 750px`}
            priority={priority}
          />
        </div>
        {imageCaption && (
          <Text
            className={classnames(styles.imageCredit, {
              ...(image.credit && {
                [styles[image.credit.theme]]: image.credit.theme,
              }),
            })}
            size={TextSizes.magSecondaryBodySml}
          >
            {imageCaption}
          </Text>
        )}
      </>
    )
  }

  return (
    <Fixture
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
    >
      <AnimateIntoView type={AnimationType.fadeIn}>
        <div
          className={classnames(styles.base, styles[orientationsClass], {
            [styles['leadImage']]: isLeadImage,
          })}
          data-testid={testID}
        >
          {artwork ? (
            <Link href={artworkHref as any}>{renderImage()}</Link>
          ) : link ? (
            <Link href={resolveLink(link)}>{renderImage()}</Link>
          ) : (
            <> {renderImage()}</>
          )}
          {artwork ? (
            <Button
              variant={ButtonVariants.textLink}
              link={artworkHref as any}
              iconLeft
              className={styles.artworkLink}
            >
              {t('buyOrSellArtwork')} {artwork.title}
            </Button>
          ) : null}
        </div>
      </AnimateIntoView>
    </Fixture>
  )
}
